import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Share2, Download, Copy, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Export dropdown for CanvasEditor in standalone (Lab) mode.
 * Provides: Post to The Block, Download PNG, Copy to Clipboard, Post as Story (soon).
 */
/**
 * compact=true: shows only a small "•••" dropdown for download/copy (used in Lab topbar
 *               alongside a dedicated "Drop to Block" fire button).
 * compact=false (default): shows full Export button with dropdown including post action.
 */
export const ExportMenu = ({ onPost, onDownload, onCopy, exporting, isDark, compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const dropdownBg = isDark
    ? 'border-white/[0.08]'
    : 'bg-white border-gray-200';
  const dropdownStyle = isDark ? { background: 'var(--surface, #151A24)' } : undefined;

  const itemClass = cn(
    'w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors',
    isDark ? 'text-[#EAE0CC] hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
  );

  return (
    <div className="relative" ref={ref}>
      {compact ? (
        /* Compact: just a small icon-only trigger for download/copy */
        <button
          onClick={() => setOpen(!open)}
          disabled={exporting}
          className="lab-topbar-icon-btn"
          title="More options"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      ) : (
        /* Full: "Export" button with dropdown */
        <button
          onClick={() => setOpen(!open)}
          disabled={exporting}
          className={cn(
            'flex items-center gap-1.5 px-4 py-1.5 text-sm font-display tracking-wider',
            isDark ? 'bg-white text-black' : 'bg-black text-white',
            exporting && 'opacity-50'
          )}
        >
          {exporting ? 'Exporting...' : 'Export'}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      )}

      {open && (
        <div
          className={cn('absolute right-0 top-full mt-1 w-52 border shadow-xl z-[120] rounded-lg overflow-hidden', dropdownBg)}
          style={dropdownStyle}
        >
          {!compact && (
            <button onClick={() => { setOpen(false); onPost(); }} className={itemClass}>
              <Share2 className="h-4 w-4 shrink-0" style={{ color: 'var(--fire, #F07B1D)' }} />
              Post to The Block
            </button>
          )}
          <button onClick={() => { setOpen(false); onDownload(); }} className={itemClass}>
            <Download className="h-4 w-4 shrink-0" />
            Download PNG
          </button>
          <button onClick={() => { setOpen(false); onCopy(); }} className={itemClass}>
            <Copy className="h-4 w-4 shrink-0" />
            Copy to Clipboard
          </button>
          <div className={cn('border-t', isDark ? 'border-white/[0.06]' : 'border-gray-100')}>
            <div className={cn(
              'flex items-center gap-3 px-4 py-3 text-sm opacity-40 cursor-not-allowed',
              isDark ? 'text-[#EAE0CC]' : 'text-gray-900'
            )}>
              <Clock className="h-4 w-4 shrink-0" />
              Post as Story
              <span className="ml-auto text-[9px] font-display tracking-wider uppercase" style={{ color: 'var(--fire, #F07B1D)' }}>
                Soon
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
